import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * Floating bar component that appears when scrolling past buy buttons on product page
 * @extends Component
 */
export class ProductFloatingBar extends Component {
  #scrollHandler = null;
  #buyButtonsElement = null;
  #threshold = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#buyButtonsElement = document.querySelector(".buy-buttons-block");

    if (!this.#buyButtonsElement) {
      return;
    }

    this.#scrollHandler = this.#handleScroll.bind(this);
    window.addEventListener("scroll", this.#scrollHandler, { passive: true });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#handleScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scrollHandler) {
      window.removeEventListener("scroll", this.#scrollHandler);
    }

    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #onVariantUpdate = (event) => {
    if (event.detail?.data?.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail?.resource;
    const titleEl = this.querySelector("[data-floating-variant-title]");

    if (!variant || !titleEl) {
      return;
    }

    const productTitle = this.dataset.productTitle || "";
    const variantTitle = variant.title && variant.title !== "Default Title" ? variant.title : "";
    titleEl.textContent = variantTitle ? `${productTitle} — ${variantTitle}` : productTitle;
  };

  #handleScroll() {
    if (!this.#buyButtonsElement) {
      return;
    }

    const buyButtonsRect = this.#buyButtonsElement.getBoundingClientRect();
    const buyButtonsScrolledPast = buyButtonsRect.bottom < -this.#threshold;
    const shouldShow = buyButtonsScrolledPast;

    if (shouldShow) {
      // Show with opacity transition
      // Remove hidden attribute first, then trigger transition on next frame
      if (this.hasAttribute("hidden")) {
        this.removeAttribute("hidden");
        // Wait for browser to process the removal before triggering transition
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.dataset.visible = "true";
          });
        });
      } else {
        // Already visible, just update the data attribute
        this.dataset.visible = "true";
      }
    } else {
      // Hide with opacity transition
      this.dataset.visible = "false";
      // Wait for transition to complete before setting hidden attribute (prevents pointer events)
      setTimeout(() => {
        if (this.dataset.visible === "false") {
          this.setAttribute("hidden", "");
        }
      }, 400); // Match CSS transition duration (0.4s)
    }
  }
}

if (!customElements.get("product-floating-bar")) {
  customElements.define("product-floating-bar", ProductFloatingBar);
}
